import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Subscription } from 'rxjs';

import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { BLOG_COPY } from '../../constants/blog.constants';
import type { BlogCommentsPage, BlogCommentView } from '../../data/blog-comments.mappers';
import { BlogCommentsService } from '../../data/blog-comments.service';
import { BlogDatePipe } from '../../pipes/blog-format.pipes';
import { ArticleCommentFormComponent } from '../article-comment-form/article-comment-form.component';

/**
 * Estado del hilo. `hidden` = 404 de la API (entrada no visible): no se pinta
 * nada, ni el formulario (B107).
 */
type ThreadState =
  | { status: 'loading' }
  | { status: 'hidden' }
  | { status: 'error' }
  | { status: 'ready'; page: BlogCommentsPage };

/**
 * Comentarios públicos de una entrada (W4, B105–B113).
 *
 * El hilo se pinta en SSR: la página 1 se pide al construir el componente y
 * viaja al navegador por `TransferState` (B106), así que el primer render de
 * cliente coincide con el del servidor. El formulario solo existe en cliente,
 * dentro de un `@defer` (B110).
 */
@Component({
  selector: 'app-article-comments',
  imports: [NgTemplateOutlet, ButtonComponent, BlogDatePipe, ArticleCommentFormComponent],
  templateUrl: './article-comments.component.html',
  styleUrl: './article-comments.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class ArticleCommentsComponent {
  readonly slug = input.required<string>();

  private readonly service = inject(BlogCommentsService);

  protected readonly copy = BLOG_COPY.comments;
  protected readonly skeletonRows = [0, 1, 2];

  protected readonly thread = signal<ThreadState>({ status: 'loading' });
  protected readonly page = computed(() => {
    const thread = this.thread();
    return thread.status === 'ready' ? thread.page : null;
  });
  protected readonly hasMore = computed(() => {
    const page = this.page();
    return page !== null && page.page < page.totalPages;
  });
  protected readonly loadingMore = signal(false);
  protected readonly loadMoreFailed = signal(false);

  /** Raíz a la que se responde (B112): un único formulario al pie. */
  protected readonly replyTo = signal<BlogCommentView | null>(null);
  protected readonly focusRequest = signal(0);
  /** 404 al enviar: la entrada dejó de admitir comentarios (B113). */
  protected readonly formClosed = signal(false);

  private readonly formSection = viewChild<ElementRef<HTMLElement>>('formSection');

  private firstPageSub: Subscription | null = null;
  private loadMoreSub: Subscription | null = null;

  constructor() {
    // La página 1 se pide ya en construcción (el SSR espera al HttpClient) y se
    // vuelve a pedir si la ficha reutiliza el componente con otro slug.
    effect(() => {
      const slug = this.slug();
      untracked(() => this.reset(slug));
    });

    inject(DestroyRef).onDestroy(() => {
      this.firstPageSub?.unsubscribe();
      this.loadMoreSub?.unsubscribe();
    });
  }

  protected retry(): void {
    this.loadFirstPage(this.slug());
  }

  protected loadMore(): void {
    const current = this.page();
    if (!current || this.loadingMore()) return;

    this.loadingMore.set(true);
    this.loadMoreFailed.set(false);
    this.loadMoreSub = this.service.list(this.slug(), current.page + 1).subscribe({
      next: (next) => {
        this.loadingMore.set(false);
        if (next === null) {
          this.thread.set({ status: 'hidden' });
          return;
        }
        // Una moderación entre páginas puede desplazar una raíz a la siguiente:
        // no se pinta dos veces.
        const seen = new Set(current.comments.map((c) => c.id));
        this.thread.set({
          status: 'ready',
          page: {
            ...next,
            comments: [...current.comments, ...next.comments.filter((c) => !seen.has(c.id))],
          },
        });
      },
      error: () => {
        this.loadingMore.set(false);
        this.loadMoreFailed.set(true);
      },
    });
  }

  /** Handler de cliente: fija la raíz, lleva al formulario y pide el foco. */
  protected reply(comment: BlogCommentView): void {
    this.replyTo.set(comment);
    this.focusRequest.update((n) => n + 1);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.formSection()?.nativeElement.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  protected cancelReply(): void {
    this.replyTo.set(null);
  }

  /**
   * 201 del formulario. H1: mientras el POST estaba en vuelo el lector pudo
   * pulsar «Responder» en otra raíz; solo se limpia `replyTo` si sigue siendo
   * la raíz a la que se respondió. Un envío raíz (`null`) no limpia nada.
   */
  protected onSent(parentId: number | null): void {
    if (parentId !== null && this.replyTo()?.id === parentId) {
      this.replyTo.set(null);
    }
  }

  protected onClosed(): void {
    this.replyTo.set(null);
    this.formClosed.set(true);
  }

  protected initial(name: string): string {
    return (Array.from(name.trim())[0] ?? '').toUpperCase();
  }

  private reset(slug: string): void {
    this.replyTo.set(null);
    this.formClosed.set(false);
    this.loadFirstPage(slug);
  }

  private loadFirstPage(slug: string): void {
    this.firstPageSub?.unsubscribe();
    this.loadMoreSub?.unsubscribe();
    this.loadingMore.set(false);
    this.loadMoreFailed.set(false);
    this.thread.set({ status: 'loading' });

    this.firstPageSub = this.service.list(slug, 1).subscribe({
      next: (page) =>
        this.thread.set(page === null ? { status: 'hidden' } : { status: 'ready', page }),
      error: () => this.thread.set({ status: 'error' }),
    });
  }
}
